import type { SessionOverMessage } from '@star-snake/shared'
import { DeathReason } from '@star-snake/shared'
import { NeonColors, neonButton, neonText, SCREEN_BASE_STYLE, fadeTransition, cosmicBackground, isTouchUiMode, neonPanel, FONTS } from '../neon-theme'
import { mountToAppShell } from '../appShell'

const DEATH_REASON_LABELS: Record<string, string> = {
  [DeathReason.COLLISION]: 'Hit another snake',
  [DeathReason.HEAD_ON]: 'Head-on collision',
  [DeathReason.RED_ZONE]: 'Caught by the red zone',
  [DeathReason.DISCONNECT]: 'Disconnected',
  [DeathReason.SESSION_END]: 'Session ended',
  [DeathReason.COLLAPSE]: 'Snake collapsed',
  [DeathReason.CHEATING]: 'Anti-cheat violation',
  [DeathReason.ADMIN_KICK]: 'Kicked by admin',
}

export interface SessionEndContext {
  deathReason?: DeathReason
  killerName?: string | null
}

export class SessionOverScreen {
  private container: HTMLDivElement
  private onContinue: (() => void) | null = null
  private hideTimerId: number | null = null

  constructor() {
    this.container = document.createElement('div')
    this.container.id = 'session-over-screen'
    this.container.style.cssText = `
      ${SCREEN_BASE_STYLE}
      background: ${NeonColors.bgDark};
      padding: 20px; box-sizing: border-box;
      opacity: 0;
      ${fadeTransition(300)}
    `
  }

  show(data: SessionOverMessage, onContinue: () => void, context?: SessionEndContext): void {
    if (this.hideTimerId !== null) { clearTimeout(this.hideTimerId); this.hideTimerId = null }
    this.onContinue = onContinue
    this.container.innerHTML = ''
    const lightweightUi = isTouchUiMode()

    // Cosmic background
    if (!lightweightUi) {
      this.container.insertAdjacentHTML('afterbegin', cosmicBackground())
    }

    const styleEl = document.createElement('style')
    styleEl.textContent = `
      @keyframes soFadeIn {
        from { opacity: 0; transform: translateY(-10px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes mapPulse {
        0%, 100% { text-shadow: 0 0 20px ${NeonColors.dangerGlow}, 0 0 40px ${NeonColors.dangerGlow}; }
        50% { text-shadow: 0 0 40px ${NeonColors.dangerGlow}, 0 0 80px ${NeonColors.dangerGlow}; }
      }
      .so-fade-in { animation: soFadeIn 0.5s ease-out forwards; opacity: 0; }
    `
    this.container.appendChild(styleEl)

    // Content wrapper
    const content = document.createElement('div')
    content.style.cssText = 'z-index: 30; display: flex; flex-direction: column; align-items: center;'

    const title = document.createElement('h1')
    title.style.cssText = `
      font-size: 2.2rem; letter-spacing: 2px; margin: 0 0 0.3rem 0;
      ${neonText(NeonColors.danger, NeonColors.dangerGlow)}
      ${lightweightUi ? '' : 'animation: mapPulse 1.5s ease-in-out infinite;'}
      font-family: ${FONTS.title};
    `
    title.className = 'so-fade-in'
    title.textContent = 'ROOM COLLAPSED'
    content.appendChild(title)

    const subtitle = document.createElement('p')
    subtitle.style.cssText = `color: ${NeonColors.textMuted}; margin: 0 0 2rem 0; font-size: 0.95rem;`
    subtitle.className = 'so-fade-in'
    if (context?.deathReason) {
      const reasonText = DEATH_REASON_LABELS[context.deathReason] ?? context.deathReason
      const killerSuffix = context.killerName ? ` by ${context.killerName}` : ''
      subtitle.textContent = `${reasonText}${killerSuffix}. The room has ended.`
    } else {
      subtitle.textContent = 'The room exploded and the session has been reset.'
    }
    content.appendChild(subtitle)

    const myStats = data.stats.length > 0 ? data.stats[0] : null

    const statsGrid = document.createElement('div')
    statsGrid.style.cssText = `
      display: flex; flex-wrap: wrap; gap: 12px; justify-content: center;
      margin-bottom: 1.5rem; max-width: 400px; opacity: 0;
    `

    if (myStats) {
      const statItems = [
        { label: 'Kills', value: String(myStats.kills), color: NeonColors.danger, glow: NeonColors.dangerGlow },
        { label: 'Max Length', value: String(myStats.maxLength), color: NeonColors.success, glow: NeonColors.successGlow },
        { label: 'Sparks', value: String(myStats.sparksCollected), color: NeonColors.gold, glow: NeonColors.goldGlow },
        { label: 'Time Alive', value: this.formatTime(myStats.timeAlive), color: NeonColors.info, glow: NeonColors.infoGlow },
        { label: 'Evacuated', value: myStats.evacuated ? 'YES' : 'NO', color: myStats.evacuated ? NeonColors.success : NeonColors.danger, glow: myStats.evacuated ? NeonColors.successGlow : NeonColors.dangerGlow },
      ]

      for (const item of statItems) {
        const box = document.createElement('div')
        box.style.cssText = `${neonPanel({ blur: !lightweightUi })} min-width: 90px; text-align: center; flex: 1 1 auto;`

        const label = document.createElement('div')
        label.style.cssText = `font-size: 0.75rem; color: ${NeonColors.textMuted};`
        label.textContent = item.label
        box.appendChild(label)

        const val = document.createElement('div')
        val.style.cssText = `font-size: 1.2rem; font-weight: bold; ${neonText(item.color, item.glow)}`
        val.textContent = item.value
        box.appendChild(val)

        statsGrid.appendChild(box)
      }
    }

    content.appendChild(statsGrid)

    const buttonsArea = document.createElement('div')
    buttonsArea.style.cssText = 'display: flex; gap: 12px; opacity: 0;'

    const menuBtn = document.createElement('button')
    menuBtn.style.cssText = `${neonButton(NeonColors.primary, NeonColors.primaryGlow)} font-size: 1.1rem;`
    menuBtn.textContent = 'RETURN TO MENU'
    menuBtn.addEventListener('mouseenter', () => {
      menuBtn.style.transform = 'scale(1.03)'
      menuBtn.style.boxShadow = `0 0 40px ${NeonColors.primaryGlow}, 0 0 60px ${NeonColors.primaryGlow}`
    })
    menuBtn.addEventListener('mouseleave', () => {
      menuBtn.style.transform = 'scale(1)'
      menuBtn.style.boxShadow = `0 0 20px ${NeonColors.primaryGlow}, 0 0 40px ${NeonColors.primaryGlow}`
    })
    menuBtn.addEventListener('click', () => { this.onContinue?.(); this.hide() })
    buttonsArea.appendChild(menuBtn)
    content.appendChild(buttonsArea)

    this.container.appendChild(content)
    mountToAppShell(this.container)
    requestAnimationFrame(() => { this.container.style.opacity = '1' })

    setTimeout(() => { statsGrid.style.opacity = '1'; statsGrid.className = 'so-fade-in' }, 500)
    setTimeout(() => { buttonsArea.style.opacity = '1'; buttonsArea.className = 'so-fade-in' }, 800)
  }

  hide(): void {
    if (this.hideTimerId !== null) { clearTimeout(this.hideTimerId); this.hideTimerId = null }
    for (const el of this.container.querySelectorAll('.cosmic-nebula, .cosmic-star-field')) {
      (el as HTMLElement).style.display = 'none'
    }
    this.container.style.opacity = '0'
    this.hideTimerId = window.setTimeout(() => {
      this.container.remove()
      this.onContinue = null
      this.hideTimerId = null
    }, 300)
  }

  dispose(): void {
    if (this.hideTimerId !== null) { clearTimeout(this.hideTimerId); this.hideTimerId = null }
    this.container.remove()
    this.onContinue = null
  }

  private formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }
}
